import { useEffect, useState, useCallback, useRef } from 'react';
import { axios, API } from '../App';
import { toast } from 'sonner';
import {
  Wifi,
  WifiOff,
  Cpu,
  Database,
  Map as MapIcon,
  Server,
  ShieldCheck,
  ShieldOff,
  RefreshCw,
  Download,
  Package,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HardDrive,
  Loader2,
} from 'lucide-react';

function StatusPill({ ok, label, neutralLabel }) {
  if (ok === null || ok === undefined) {
    return <span className="text-zinc-500 text-[10px] font-mono uppercase">—</span>;
  }
  const color = ok
    ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
    : 'text-amber-300 border-amber-500/40 bg-amber-500/10';
  return (
    <span className={`px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest rounded-sm ${color}`}>
      {ok ? <CheckCircle2 size={10} className="inline mr-1" /> : <XCircle size={10} className="inline mr-1" />}
      {ok ? label : neutralLabel || 'OFFLINE'}
    </span>
  );
}

function Card({ icon: Icon, title, subtitle, accent, children, testid }) {
  return (
    <div className={`bg-zinc-900/40 border border-zinc-800 rounded-sm p-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className={`text-${accent}-400`} />
          <div>
            <div className="text-sm font-bold uppercase tracking-tight text-zinc-100">{title}</div>
            {subtitle && <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{subtitle}</div>}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[11px] font-mono py-1 border-b border-zinc-900 last:border-b-0">
      <span className="text-zinc-500 uppercase tracking-widest text-[9px]">{k}</span>
      <span className="text-zinc-200">{v}</span>
    </div>
  );
}

export default function SystemCheck() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get('/system/info');
      setInfo(data);
      setLastRun(new Date());
    } catch (err) {
      console.warn('system/info probe failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadSource = async (format = 'zip') => {
    setDownloading(true);
    const t = toast.loading(`Assembling source bundle (${format.toUpperCase()})…`);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/system/bundle/source?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const fallback = format === 'zip' ? 'brahm-kavach-source.zip' : 'brahm-kavach-source.tar.gz';
      const filename = m ? m[1] : fallback;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${(blob.size / 1024 / 1024).toFixed(1)} MB → ${filename}`, { id: t });
    } catch (e) {
      toast.error(`Bundle download failed: ${e.message}`, { id: t });
    } finally {
      setDownloading(false);
    }
  };

  // ── Air-gap bundle (background job) ─────────────────────────────────
  const [airgap, setAirgap] = useState(null);
  const [includeOllama, setIncludeOllama] = useState(false);
  const [airgapFormat, setAirgapFormat] = useState('zip');
  const [airgapDownloading, setAirgapDownloading] = useState(false);
  const airgapPollRef = useRef(null);

  const pollAirgap = useCallback(async () => {
    try {
      const { data } = await axios.get('/system/bundle/airgap/status');
      setAirgap(data);
      if (data.state !== 'building' && airgapPollRef.current) {
        clearInterval(airgapPollRef.current);
        airgapPollRef.current = null;
      }
    } catch (err) {
      console.warn('airgap status poll failed:', err.message);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(pollAirgap);
    return () => {
      if (airgapPollRef.current) clearInterval(airgapPollRef.current);
    };
  }, [pollAirgap]);

  const startAirgap = async () => {
    const t = toast.loading(`Starting air-gap build (${airgapFormat.toUpperCase()})…`);
    try {
      const { data } = await axios.post('/system/bundle/airgap/start', {
        include_ollama: includeOllama,
        archive_format: airgapFormat,
      });
      setAirgap(data);
      if (data.started) {
        toast.success(`Air-gap ${airgapFormat.toUpperCase()} build started — this can take several minutes`, { id: t });
        // poll every 2 s while building
        if (airgapPollRef.current) clearInterval(airgapPollRef.current);
        airgapPollRef.current = setInterval(pollAirgap, 2000);
      } else {
        toast.error(`Cannot start: ${data.reason || 'see status'}`, { id: t });
      }
    } catch (e) {
      toast.error(`Start failed: ${e.response?.data?.detail || e.message}`, { id: t });
    }
  };

  const downloadAirgap = async () => {
    setAirgapDownloading(true);
    const t = toast.loading('Streaming air-gap bundle…');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/system/bundle/airgap/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : 'brahm-kavach-airgap.tar';
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${(blob.size / 1024 / 1024 / 1024).toFixed(2)} GB → ${filename}`, { id: t });
    } catch (e) {
      toast.error(`Download failed: ${e.message}`, { id: t });
    } finally {
      setAirgapDownloading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(() => refresh());
    const id = setInterval(() => refresh(true), 8000);
    return () => clearInterval(id);
  }, [refresh]);

  const wanOnline = info?.wan?.online;
  const llmOk = info?.llm?.ok;
  const mode = info?.llm?.mode;
  const overallOffline = info && !wanOnline;
  const offlineReady = info?.offline_ready;

  return (
    <div className="flex flex-col h-full bg-zinc-950" data-testid="system-check-page">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
              <Server size={20} className="text-sky-400" />
              System Check
            </h1>
            <p className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mt-0.5">
              Offline readiness · LLM provider · Tile policy · Licence
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono uppercase text-zinc-400">
            {lastRun && <span>updated {lastRun.toLocaleTimeString()}</span>}
            <button
              onClick={() => refresh(false)}
              disabled={loading}
              className="px-3 py-1.5 border border-zinc-700 hover:border-sky-500/60 hover:text-sky-300 rounded-sm flex items-center gap-1.5 disabled:opacity-50"
              data-testid="system-refresh-button"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Top banner */}
        <div
          className={`flex items-center justify-between px-5 py-4 rounded-sm border-2 ${
            !info
              ? 'border-zinc-700 bg-zinc-900/60'
              : overallOffline
              ? 'border-amber-500/60 bg-amber-500/10'
              : 'border-emerald-500/40 bg-emerald-500/5'
          }`}
          data-testid="connectivity-banner"
        >
          <div className="flex items-center gap-4">
            {!info && <AlertTriangle size={36} className="text-zinc-500" />}
            {info && wanOnline && <Wifi size={36} className="text-emerald-400" />}
            {info && !wanOnline && <WifiOff size={36} className="text-amber-400" />}
            <div>
              <div
                className={`text-3xl font-black tracking-tighter ${
                  !info ? 'text-zinc-400' : wanOnline ? 'text-emerald-300' : 'text-amber-300'
                }`}
                data-testid="connectivity-status"
              >
                {!info ? '—' : wanOnline ? 'WAN OK' : 'WAN OFFLINE'}
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">
                {info && wanOnline && 'Cloud reachable — full LLM features available'}
                {info && !wanOnline && 'Air-gapped — all sensor + engagement features remain operational'}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Build</div>
            <div className="text-sky-300 font-mono text-sm">
              v{info?.version || '—'} · {info?.build || 'dev'}
            </div>
            <div className="text-[10px] font-mono text-zinc-500">{info?.deploy_mode || '—'}</div>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {!info && (
          <div className="flex items-center justify-center h-48 text-zinc-500 font-mono text-sm">
            Probing system…
          </div>
        )}

        {info && (
          <>
            {/* Connectivity + Subsystems */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card icon={Wifi} title="WAN" subtitle="Public internet" accent={wanOnline ? 'emerald' : 'amber'} testid="card-wan">
                <Row k="Cloud reachable" v={<StatusPill ok={wanOnline} label="ONLINE" />} />
                <Row k="LLM endpoint" v={<StatusPill ok={info.wan.llm_endpoint_reachable} label="REACHABLE" />} />
                <Row k="Tile CDN" v={<StatusPill ok={info.wan.tile_server_reachable} label="REACHABLE" />} />
              </Card>

              <Card icon={Cpu} title="LLM Provider" subtitle={info.llm.mode.toUpperCase()} accent={llmOk ? 'emerald' : 'amber'} testid="card-llm">
                <Row k="Mode" v={<span className="text-sky-300">{info.llm.state}</span>} />
                <Row k="Healthy" v={<StatusPill ok={info.llm.ok} label="READY" neutralLabel="DEGRADED" />} />
                {info.llm.ollama && (
                  <>
                    <Row k="Ollama host" v={<span className="text-zinc-300 text-[10px]">{info.llm.ollama.host}</span>} />
                    <Row
                      k="Models"
                      v={
                        <span className="text-zinc-300 text-[10px]">
                          {info.llm.ollama.models?.length ? info.llm.ollama.models.join(', ') : 'none pulled'}
                        </span>
                      }
                    />
                  </>
                )}
              </Card>

              <Card icon={MapIcon} title="Map Tiles" subtitle="Satellite layer" accent={info.tiles.online ? 'emerald' : 'amber'} testid="card-tiles">
                <Row k="Tile CDN" v={<StatusPill ok={info.tiles.online} label="ONLINE" />} />
                <Row k="Policy" v={<span className="text-zinc-300">{info.tiles.policy}</span>} />
                <Row
                  k="Offline note"
                  v={<span className="text-zinc-500 text-[10px]">Vector overlay still renders without WAN</span>}
                />
              </Card>

              <Card icon={Database} title="Database" subtitle="MongoDB" accent="sky" testid="card-db">
                <Row k="Locality" v={<span className={info.mongo.local ? 'text-emerald-300' : 'text-amber-300'}>{info.mongo.local ? 'LOCAL' : 'REMOTE'}</span>} />
                <Row k="Air-gap friendly" v={<StatusPill ok={info.mongo.local} label="YES" neutralLabel="NO" />} />
              </Card>
            </div>

            {/* Air-gap summary */}
            <div
              className={`border-2 rounded-sm p-5 flex items-center justify-between ${
                offlineReady ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-zinc-800 bg-zinc-900/40'
              }`}
              data-testid="airgap-card"
            >
              <div className="flex items-center gap-4">
                {offlineReady ? (
                  <ShieldCheck size={32} className="text-emerald-400" />
                ) : (
                  <ShieldOff size={32} className="text-zinc-500" />
                )}
                <div>
                  <div className={`text-lg font-bold uppercase tracking-tight ${offlineReady ? 'text-emerald-300' : 'text-zinc-300'}`}>
                    {offlineReady ? 'Air-Gap Ready' : wanOnline ? 'Online Mode' : 'Air-Gap Partial'}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                    {offlineReady && 'Mongo local · LLM local/offline · all features operational without WAN.'}
                    {!offlineReady && wanOnline && 'Currently operating with cloud LLM + tile CDN — set LLM_MODE=ollama or offline for air-gap.'}
                    {!offlineReady && !wanOnline && mode === 'emergent' && 'WAN unavailable but LLM_MODE=emergent. Switch to LLM_MODE=ollama or offline.'}
                  </div>
                </div>
              </div>
              <div className="text-right text-[11px] font-mono text-zinc-500">
                <div>Licence</div>
                <div className="text-zinc-300">{info.license}</div>
              </div>
            </div>

            {/* Offline install — one-click + CLI */}
            <Card icon={Package} title="Offline Install" subtitle="Downloadable bundles" accent="amber" testid="card-install">
              <div className="space-y-3 text-[11px] font-mono text-zinc-300">
                {/* One-click source download */}
                <div className="bg-zinc-900/60 border border-amber-500/30 rounded-sm p-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm text-amber-200 font-bold uppercase tracking-tight mb-0.5">
                      One-click source bundle
                    </div>
                    <div className="text-[10px] text-zinc-500 leading-snug">
                      Streams a freshly built archive (<code>.zip</code> or <code>.tar.gz</code>)
                      straight from this server. Recipient runs
                      <code className="text-amber-300"> ./start.sh</code> after installing Docker.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => downloadSource('zip')}
                      disabled={downloading}
                      data-testid="download-source-zip-button"
                      className="px-4 py-2 bg-amber-500/20 border border-amber-500/60 hover:bg-amber-500/30 text-amber-200 rounded-sm flex items-center gap-2 font-mono uppercase tracking-widest text-xs disabled:opacity-50 whitespace-nowrap"
                    >
                      <Download size={14} className={downloading ? 'animate-pulse' : ''} />
                      {downloading ? 'Building…' : '.zip'}
                    </button>
                    <button
                      onClick={() => downloadSource('tar')}
                      disabled={downloading}
                      data-testid="download-source-button"
                      title="Download as gzipped tarball (Linux/macOS friendly)"
                      className="px-3 py-2 border border-zinc-700 hover:border-amber-500/50 text-zinc-400 hover:text-amber-300 rounded-sm flex items-center gap-1.5 font-mono uppercase tracking-widest text-xs disabled:opacity-50 whitespace-nowrap"
                    >
                      <Download size={12} />
                      .tar.gz
                    </button>
                  </div>
                </div>

                {/* CLI references */}
                <div className="flex items-center justify-between border-b border-zinc-900 pb-2">
                  <span>Source bundle (CLI)</span>
                  <code className="text-amber-300">deploy/make-source-bundle.sh</code>
                </div>
                <div className="flex items-center justify-between border-b border-zinc-900 pb-2">
                  <span>Air-gap bundle with pre-built images (~3 GB)</span>
                  <code className="text-amber-300">deploy/make-airgap-bundle.sh</code>
                </div>
                <div className="flex items-center justify-between">
                  <span>Cross-platform launcher</span>
                  <code className="text-amber-300">./start.sh / start.bat</code>
                </div>
                <div className="text-[10px] text-zinc-500 leading-relaxed mt-2 flex items-start gap-2">
                  <Download size={10} className="mt-0.5" />
                  Both bundles include MongoDB, backend, frontend and (optional) Ollama with
                  llama3.2:1b. See <code className="text-zinc-400">OFFLINE_INSTALL.md</code> for
                  the full procedure including Windows/macOS Docker Desktop notes.
                </div>
              </div>
            </Card>

            {/* Air-gap bundle — background build */}
            <Card icon={HardDrive} title="Air-Gap Bundle" subtitle="~3 GB · ships docker images" accent="red" testid="card-airgap">
              {(() => {
                const ag = airgap || {};
                const docker = ag.docker || {};
                const state = ag.state || 'idle';
                const building = state === 'building';
                const ready = state === 'ready';
                const hasError = state === 'error';
                const progressPct = Math.round((ag.progress || 0) * 100);
                return (
                  <div className="space-y-3 text-[11px] font-mono">
                    {/* Docker availability */}
                    <div className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 px-3 py-2 rounded-sm">
                      <span className="text-zinc-400 uppercase tracking-widest text-[9px]">
                        Docker socket
                      </span>
                      <span className="flex items-center gap-2">
                        {docker.ok ? (
                          <span className="text-emerald-300 flex items-center gap-1">
                            <CheckCircle2 size={11} /> available
                            {docker.version && <span className="text-zinc-500">· v{docker.version}</span>}
                          </span>
                        ) : (
                          <span className="text-amber-300 flex items-center gap-1" data-testid="airgap-docker-unavailable">
                            <XCircle size={11} /> unavailable
                          </span>
                        )}
                      </span>
                    </div>
                    {!docker.ok && (
                      <div className="text-[10px] text-amber-400/80 leading-snug px-2 border-l-2 border-amber-500/40">
                        {docker.why}
                      </div>
                    )}

                    {/* Disk + options + action */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 text-zinc-400 flex-wrap">
                        <span className="text-[9px] uppercase tracking-widest text-zinc-500">
                          Disk free
                        </span>
                        <span className={`text-${(ag.disk_free_gb || 0) > 5 ? 'emerald' : 'red'}-300`}>
                          {ag.disk_free_gb ?? '—'} GB
                        </span>
                        <label className="flex items-center gap-2 cursor-pointer select-none ml-2">
                          <input
                            type="checkbox"
                            checked={includeOllama}
                            onChange={(e) => setIncludeOllama(e.target.checked)}
                            disabled={building || ready}
                            className="accent-amber-500"
                            data-testid="airgap-include-ollama"
                          />
                          <span className="text-zinc-400">include Ollama (+1 GB)</span>
                        </label>
                        {/* Archive format toggle */}
                        <div
                          className={`flex items-center gap-1 border border-zinc-700 rounded-sm overflow-hidden ${building || ready ? 'opacity-50' : ''}`}
                          data-testid="airgap-format-toggle"
                        >
                          {['zip', 'tar'].map((f) => (
                            <button
                              key={f}
                              type="button"
                              onClick={() => !building && !ready && setAirgapFormat(f)}
                              disabled={building || ready}
                              data-testid={`airgap-format-${f}`}
                              className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest disabled:cursor-not-allowed ${
                                airgapFormat === f
                                  ? 'bg-amber-500/20 text-amber-200'
                                  : 'text-zinc-500 hover:text-zinc-300'
                              }`}
                            >
                              .{f}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!building && !ready && (
                          <button
                            onClick={startAirgap}
                            disabled={!docker.ok}
                            data-testid="airgap-start-button"
                            className="px-4 py-2 bg-red-500/15 border border-red-500/50 hover:bg-red-500/25 text-red-200 rounded-sm flex items-center gap-2 font-mono uppercase tracking-widest text-xs disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            <Package size={14} /> Build .{airgapFormat}
                          </button>
                        )}
                        {building && (
                          <span className="px-3 py-1.5 border border-amber-500/50 text-amber-200 rounded-sm flex items-center gap-1.5 text-xs uppercase tracking-widest">
                            <Loader2 size={12} className="animate-spin" /> Building…
                          </span>
                        )}
                        {ready && (
                          <button
                            onClick={downloadAirgap}
                            disabled={airgapDownloading}
                            data-testid="airgap-download-button"
                            className="px-4 py-2 bg-emerald-500/20 border border-emerald-500/60 hover:bg-emerald-500/30 text-emerald-200 rounded-sm flex items-center gap-2 font-mono uppercase tracking-widest text-xs disabled:opacity-50 whitespace-nowrap"
                          >
                            <Download size={14} className={airgapDownloading ? 'animate-pulse' : ''} />
                            {airgapDownloading ? 'Streaming…' : `Download ${(ag.size_mb / 1024).toFixed(2)} GB`}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Progress */}
                    {(building || ready || hasError) && (
                      <div className="space-y-1.5" data-testid="airgap-progress">
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-zinc-400 uppercase tracking-widest">
                            {ag.stage || (ready ? 'complete' : '—')}
                          </span>
                          <span className="text-zinc-400">
                            stage {ag.stage_idx}/{ag.stage_total} · {(ag.size_mb || 0).toFixed(1)} MB · {ag.elapsed_s}s
                          </span>
                        </div>
                        <div className="h-1.5 bg-zinc-800 rounded-sm overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${
                              hasError ? 'bg-red-500' : ready ? 'bg-emerald-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${ready ? 100 : progressPct}%` }}
                            data-testid="airgap-progress-bar"
                          />
                        </div>
                        {hasError && (
                          <div className="text-[10px] text-red-300 leading-snug">
                            ERROR: {ag.error}
                          </div>
                        )}
                        {(ag.logs || []).length > 0 && (
                          <details className="text-[10px] mt-1">
                            <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">
                              build log ({ag.logs.length})
                            </summary>
                            <pre className="bg-zinc-950 border border-zinc-800 rounded-sm p-2 mt-1 max-h-40 overflow-y-auto text-zinc-400">
                              {ag.logs.join('\n')}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}

                    <div className="text-[10px] text-zinc-500 leading-relaxed flex items-start gap-2">
                      <HardDrive size={10} className="mt-0.5" />
                      Bundles the source layout plus <code>docker save</code>-d images
                      (brahm-kavach + mongo, optionally ollama). On the recipient: extract,
                      run <code className="text-zinc-400">./load-images.sh</code>, then
                      <code className="text-zinc-400"> ./start.sh offline</code>.
                    </div>
                  </div>
                );
              })()}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
